import React, { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { toast, QueryLoader } from "@jsq/ds/core";
import {
  useCreateIndividualProfileMutation,
  useCreateInstitutionProfileMutation,
  useGetOwnersQuery,
  useUpdateInstitutionOwnerProfileMutation,
  useUpdateIndividualProfileOwnerBasicDetailsMutation,
  useUpdateCaseProfileOwnersCompletedMutation,
  useRemoveProfileFromCaseMutation,
  ComplianceObjectType,
} from "../../graphql/supergraph.index";
import { BeneficialOwnerSummaryList } from "../BeneficialOwnerSummaryList";
import { Owner } from "../BeneficialOwnerSummaryList/types";
import { CreateProfileModal } from "../CreateProfileModal";
import { ProfileFormValues, ProfileType } from "../CreateProfileModal/types";
import { DeleteProfileModal } from "../DeleteProfileModal";
import { ProfileToDelete } from "../DeleteProfileModal/types";
import { BeneficialOwnerScreenProps, ProfileTypeObject } from "./types";

export const BeneficialOwnersScreen: React.FC<BeneficialOwnerScreenProps> = ({
  caseResult,
  basePath,
  arenaId,
}) => {
  const { profileId } = useParams<{ profileId: string }>();
  const history = useHistory();
  const caseId = caseResult?.complianceCasesByObjectId[0].id;

  // State for the create/edit profile modal
  const [showCreateProfileModal, setShowCreateProfileModal] = useState(false);
  const [showDeleteProfileModal, setShowDeleteProfileModal] = useState(false);
  const [editingOwnerId, setEditingOwnerId] = useState<string | null>(null);
  const [ownerBeingDeleted, setOwnerBeingDeleted] = useState<ProfileToDelete | null>(null); // ProfileRelationshipType

  const results = useGetOwnersQuery({
    variables: {
      caseId: Number(caseId),
    },
    skip: !caseId,
  });
  const { data } = results;

  const [updateOwnersCompleted] = useUpdateCaseProfileOwnersCompletedMutation({
    onError: () => toast.error("Error marking owners as completed."),
    onCompleted: () => {
      toast.success("Owners marked as completed successfully");
    },
    refetchQueries: ["GetComplianceCasesByObjectId"],
    awaitRefetchQueries: true,
  });

  // Get the entity name from the case result
  const entityName = caseResult?.complianceCasesByObjectId[0].investingProfile?.legalName || "";

  // Helper function to format the name based on compliance object type
  const formatName = (profile: ProfileTypeObject | null) => {
    if (!profile) return "";
    return profile.complianceObjectType !== "individual"
      ? profile.legalName || ""
      : `${profile.firstName || ""} ${profile.middleName || ""} ${profile.lastName || ""}`.trim();
  };

  // Transform the data into the format expected by ControllerIndividualSelection
  const owners: Owner[] =
    data?.complianceCase?.investingProfile?.beneficialOwners.map((person) => {
      return {
        id: person?.childProfile?.id?.toString() || "",
        name: formatName(person.childProfile),
        type:
          person.childProfile.complianceObjectType === "individual"
            ? "Individual"
            : "Institution",
        relationship: "Owner",
        ownershipPercentage: person.percentOwnership || 0,
      };
    }) || [];

  const handleCompleted = async () => {
    await updateOwnersCompleted({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
        ownersCompleted: true,
      },
    });
    // Navigate back to overview
    history.push(`${basePath}/overview/${profileId}`);
  };

  const handleBack = () => {
    // Navigate back to overview
    history.push(`${basePath}/overview/${profileId}`);
  };

  const handleEdit = (id: string) => {
    setEditingOwnerId(id);
    setShowCreateProfileModal(true);
  };

  const handleDelete = (id: string) => {
    // Get all the info we'll need to pass to the delete modal
    const ownerBeingDeletedData = id
      ? data?.complianceCase?.investingProfile?.beneficialOwners.find(
          (profile) => profile.childProfile.id.toString() === id,
        )
      : null;

    if (!ownerBeingDeletedData?.childProfile?.id) {
      toast.error("Could not find profile to delete");
      return;
    }

    const profileAndDescendants = (
      (data?.complianceCase?.profiles || []) as Array<{
        path?: string;
        parentRelationships: Array<{ relationshipType: string }>;
      }>
    ).filter((profile) =>
      profile?.path?.includes(`/${ownerBeingDeletedData.childProfile.id}/`),
    );
    const isOrHasPaymentBeneficiary = profileAndDescendants?.some((profile) =>
      profile?.parentRelationships.some(
        (relationship) => relationship.relationshipType === "payment_beneficiary",
      ),
    );
    const re = new RegExp(`\\/${ownerBeingDeletedData.childProfile.id}\\/\\d`);
    const descendantNames = profileAndDescendants
      ?.filter((profile) => re.test(profile?.path || ""))
      .map((profile) => {
        const profileWithType = {
          ...profile,
          complianceObjectType: "individual" as ComplianceObjectType,
          id: 0,
          firstName: null,
          lastName: null,
          middleName: null,
          legalName: null,
          addresses: [],
        };
        return formatName(profileWithType);
      });

    setOwnerBeingDeleted({
      id: ownerBeingDeletedData.childProfile.id.toString(),
      name: formatName(ownerBeingDeletedData.childProfile),
      isOrHasPaymentBeneficiary: isOrHasPaymentBeneficiary || false,
      descendantNames: descendantNames || [],
    });
    setShowDeleteProfileModal(true);
  };

  const handleConfirmDelete = async (caseId: number, profileId: string) => {
    await removeProfileFromCase({
      variables: {
        caseId: Number(caseId),
        profileId: Number(profileId),
      },
    });
  };

  // Mutation for creating an individual profile
  const [updateIndividualProfile] = useUpdateIndividualProfileOwnerBasicDetailsMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Owner updated successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetOwners"],
    awaitRefetchQueries: true,
  });

  const [updateInstitutionProfile] = useUpdateInstitutionOwnerProfileMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Owner updated successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetOwners"],
    awaitRefetchQueries: true,
  });

  const [createIndividualProfile] = useCreateIndividualProfileMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Owner created successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetOwners"],
    awaitRefetchQueries: true,
  });

  const [createInstitutionProfile] = useCreateInstitutionProfileMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Owner created successfully");
      setShowCreateProfileModal(false);
    },
    refetchQueries: ["GetOwners"],
    awaitRefetchQueries: true,
  });

  const [removeProfileFromCase] = useRemoveProfileFromCaseMutation({
    onError: (e) => toast.error(e.message),
    onCompleted: () => {
      toast.success("Profile(s) deleted");
      setShowDeleteProfileModal(false);
    },
    refetchQueries: ["GetOwners"],
    awaitRefetchQueries: true,
  });

  const editOwner = async (values: ProfileFormValues) => {
    try {
      if (arenaId === undefined) {
        toast.error("An error has occurred, please try again later.");
        return;
      }
      if (!editingOwnerId || !editingValues) {
        toast.error("An error has occurred, please try again later.");
        return;
      }
      // Creating a new owner
      if (editingValues.profileType === "Individual") {
        const ownershipPercentage =
          Number(values.ownershipPercentage) || Number(editingValues?.ownershipPercentage) || 0;

        await updateIndividualProfile({
          variables: {
            data: {
              caseId: Number(caseId),
              firstName: values.firstName || editingValues?.firstName || "",
              middleName: values.middleName || editingValues?.middleName || "",
              lastName: values.lastName || editingValues?.lastName || "",
              addresses: [],
              relationshipType: ["owner"],
              ownershipPercentage,
            },
            profileId: Number(editingOwnerId),
            parentProfileId: Number(profileId),
          },
        });
      } else {
        const ownershipPercentage =
          Number(values.ownershipPercentage) || Number(editingValues?.ownershipPercentage) || 0;

        await updateInstitutionProfile({
          variables: {
            data: {
              caseId: Number(caseId),
              legalBusinessName:
                values.legalBusinessName || editingValues?.legalBusinessName || "",
              addresses: [],
              relationshipType: ["owner"],
              ownershipPercentage,
            },
            profileId: Number(editingOwnerId),
            parentProfileId: Number(profileId),
          },
        });
      }
    } catch (error) {
      toast.error("An error occurred while updating the owner");
    }
  };

  const createOwner = async (values: ProfileFormValues) => {
    if (arenaId === undefined) {
      toast.error("An error has occurred, please try again later.");
      return;
    }

    // Creating a new owner
    if (values.profileType === "Individual") {
      await createIndividualProfile({
        variables: {
          arenaAccessId: Number(arenaId),
          data: {
            caseId: Number(caseId),
            firstName: values.firstName || "",
            middleName: values.middleName,
            lastName: values.lastName || "",
            relationshipType: ["owner"],
            addresses: [],
            ownershipPercentage: values.ownershipPercentage || 0,
          },
          parentProfileId: Number(profileId),
        },
      });
    } else {
      await createInstitutionProfile({
        variables: {
          arenaAccessId: Number(arenaId),
          data: {
            caseId: Number(caseId),
            legalBusinessName: values.legalBusinessName || "",
            relationshipType: ["owner"],
            addresses: [],
            ownershipPercentage: values.ownershipPercentage || 0,
          },
          parentProfileId: Number(profileId),
        },
      });
    }
  };

  // Find the owner being edited
  const ownerBeingEdited = editingOwnerId
    ? data?.complianceCase?.investingProfile?.beneficialOwners.find(
        (person) => person.childProfile.id.toString() === editingOwnerId,
      )
    : null;

  // Prepare initial values for the form when editing
  const editingValues = ownerBeingEdited
    ? {
        profileType: (ownerBeingEdited.childProfile.complianceObjectType === "individual"
          ? "Individual"
          : "Entity") as ProfileType,
        firstName: ownerBeingEdited.childProfile.firstName || "",
        middleName: ownerBeingEdited.childProfile.middleName || "",
        lastName: ownerBeingEdited.childProfile.lastName || "",
        fullName: [
          ownerBeingEdited.childProfile.firstName,
          ownerBeingEdited.childProfile.middleName,
          ownerBeingEdited.childProfile.lastName,
        ]
          .filter(Boolean)
          .join(" "),
        legalBusinessName: ownerBeingEdited.childProfile.legalName || "",
        ownershipPercentage: ownerBeingEdited.percentOwnership || 0,
      }
    : undefined;

  return (
    <QueryLoader result={results}>
      <BeneficialOwnerSummaryList
        entityName={entityName}
        owners={owners}
        onAddOwner={() => {
          // Reset editing state when adding a new owner
          setEditingOwnerId(null);
          setShowCreateProfileModal(true);
        }}
        onCompleted={handleCompleted}
        onBack={handleBack}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />
      <CreateProfileModal
        title={editingOwnerId ? "Edit Owner" : "Add Owner"}
        show={showCreateProfileModal}
        showOwnershipPercentage={true}
        profileTypeLabel="Owner"
        initialValues={editingValues}
        onInformationSubmitted={async (values: ProfileFormValues) => {
          if (editingOwnerId) {
            await editOwner(values);
          } else {
            await createOwner(values);
          }
        }}
        onClose={() => {
          setShowCreateProfileModal(false);
          setEditingOwnerId(null);
        }}
      />
      <DeleteProfileModal
        profile={ownerBeingDeleted}
        caseId={Number(caseId)}
        show={showDeleteProfileModal}
        onClose={() => setShowDeleteProfileModal(false)}
        onDelete={handleConfirmDelete}
      />
    </QueryLoader>
  );
};
